// Payload CMS API utilities for trippedAdvisors
// Based on your working yumaway-website pattern

// Environment detection for React app (mimicking your env.isDev/isProd)
const env = {
  isDev: process.env.REACT_APP_ENV === "development" || process.env.NODE_ENV === "development",
  isProd: process.env.REACT_APP_ENV === "production",
};

// Use production CMS for API calls, but keep env logic for filtering
const API_URL = process.env.REACT_APP_PAYLOAD_API_URL;

function getFormattedDate(dateString) {
  if (!dateString) return "";
  try {
    // Using same format as your original: "d MMMM, yyyy" -> "9 April, 2024"
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  } catch (error) {
    console.error("Invalid date:", dateString);
    return "";
  }
}

export async function fetchBlogsByModality(modality) {
  if (!modality) return [];

  const query = new URLSearchParams({
    depth: "1",
    sort: "-publishedDate",
  });

  if (env.isDev) {
    query.append("where[and][0][company][equals]", "almadelic");
    query.append("where[and][1][or][0][stage][equals]", "review");
    query.append("where[and][1][or][1][stage][equals]", "published");
    query.append("where[and][1][or][2][stage][equals]", "draft"); // remove later?
    query.append("where[and][2][slug][like]", `${modality}/blog/`);
  }

  if (env.isProd) {
    query.append("where[and][0][company][equals]", "almadelic");
    query.append("where[and][1][stage][equals]", "published");
    query.append("where[and][2][slug][like]", `${modality}/blog/`);
  }

  const res = await fetch(`${API_URL}/api/blogs?${query}`);
  if (!res.ok) throw new Error("Failed to fetch blogs by modality");

  const data = await res.json();
  const blogs = data.docs || [];

  // If we hit the limit, fetch more
  if (data.totalDocs > blogs.length) {
    const newQuery = new URLSearchParams(query);
    newQuery.set("limit", data.totalDocs.toString());

    const moreRes = await fetch(`${API_URL}/api/blogs?${newQuery}`);
    if (moreRes.ok) {
      const moreData = await moreRes.json();
      return moreData.docs.map((blog) => ({
        ...blog,
        formattedDate: getFormattedDate(blog.publishedDate || blog.updatedAt || blog.createdAt),
      }));
    }
  }

  return blogs.map((blog) => ({
    ...blog,
    formattedDate: getFormattedDate(blog.publishedDate || blog.updatedAt || blog.createdAt),
  }));
}
export async function fetchAllBlogs() {
  const query = new URLSearchParams({
    depth: "1",
    sort: "-publishedDate",
  });

  console.log("h")

  if (env.isDev) {
    query.append("where[and][0][company][equals]", "almadelic");
    query.append("where[and][1][or][0][stage][equals]", "review");
    query.append("where[and][1][or][1][stage][equals]", "published");
  }

  if (env.isProd) {
    query.append("where[company][equals]", "almadelic");
    query.append("where[stage][equals]", "published");
  }

  const res = await fetch(`${API_URL}/api/blogs?${query}`);
  if (!res.ok) throw new Error("Failed to fetch blogs");

  const data = await res.json();
  const blogs = data.docs || [];

  // If we hit the limit, fetch more
  if (data.totalDocs > blogs.length) {
    // Fetch with higher limit
    const newQuery = new URLSearchParams(query);
    newQuery.set("limit", data.totalDocs.toString());

    const moreRes = await fetch(`${API_URL}/api/blogs?${newQuery}`);
    if (moreRes.ok) {
      const moreData = await moreRes.json();
      return moreData.docs.map((blog) => ({
        ...blog,
        formattedDate: getFormattedDate(blog.publishedDate || blog.updatedAt || blog.createdAt),
      }));
    }
  }

  return blogs.map((blog) => ({
    ...blog,
    formattedDate: getFormattedDate(blog.updatedAt || blog.createdAt),
  }));
}

export async function fetchBlogById(id) {
  const res = await fetch(`${API_URL}/api/blogs/${id}?depth=1`);
  if (!res.ok) return null;

  const blog = await res.json();

  if (!blog) return null;

  return {
    ...blog,
    formattedDate: getFormattedDate(blog.updatedAt || blog.createdAt),
  };
}

export async function fetchBlogBySlug(slug, modalityType) {
  const query = new URLSearchParams({
    depth: "1",
  });

  // Set slug filter
  if (modalityType) {
    query.append("where[and][0][slug][equals]", `/${modalityType}/blog/${slug}`);
  } else {
    query.append("where[and][0][slug][equals]", `/${slug}`);
  }

  console.log("is before for query params")

  // Add stage filtering
  if (env.isDev) {
    console.log("is in dev for query params")
    query.append("where[and][1][or][0][stage][equals]", "published");
    query.append("where[and][1][or][1][stage][equals]", "review");
  } else if (env.isProd) {
    console.log("is in prod for query params")
    query.append("where[and][1][stage][equals]", "published");
  }

  const res = await fetch(`${API_URL}/api/blogs?${query}`);

  if (!res.ok) {
    return null;
  }

  const data = await res.json();
  const blog = data.docs?.[0];

  if (!blog) return null;

  return {
    ...blog,
    formattedDate: getFormattedDate(blog.updatedAt || blog.createdAt),
  };
}

// Helper function to get image URL from Payload media object
export function getImageUrl(media) {
  if (!media) return null;
  if (typeof media === "string") return media;

  // Handle Payload media object
  if (media.url) {
    // For images, always use the production URL regardless of API_URL
    const imageBaseUrl = process.env.REACT_APP_PAYLOAD_API_URL;
    return media.url.startsWith("http") ? media.url : `${imageBaseUrl}${media.url}`;
  }

  return null;
}

// Helper function to get author avatar URL
export function getAuthorAvatarUrl(author) {
  if (!author || !author.avatar) return null;
  return getImageUrl(author.avatar);
}

// Legacy compatibility - for search functionality
export function filterBlogsBySearch(blogs, searchTerm) {
  if (!searchTerm) return blogs;

  const term = searchTerm.toLowerCase();
  return blogs.filter(
    (blog) =>
      blog.title?.toLowerCase().includes(term) ||
      blog.description?.toLowerCase().includes(term) ||
      blog.metaDescription?.toLowerCase().includes(term) ||
      (blog.keywords && blog.keywords.some((keyword) => keyword.toLowerCase().includes(term)))
  );
}

// Export the utilities object for convenience
export const payloadAPI = {
  fetchAllBlogs,
  fetchBlogById,
  fetchBlogBySlug,
  getImageUrl,
  getAuthorAvatarUrl,
  filterBlogsBySearch,
};

export default payloadAPI;
